import { create } from 'zustand';

import { centsFromDollarsStringOrNull, centsFromDollarsStringOrZero } from 'models/dollars';
import { numberFromFixedPointString } from 'models/fixedPoint';
import type { EventPositionVo, PositionVo } from 'models/index';
import type { PositionDTO, WsMessage, WsResponse } from 'models/ws';
import type { PositionScalars } from 'queries/portfolioEventTransform';
import { getSubaccountNumber, useSubAccountStore } from 'stores/zustand/subAccountStore';
import { eventContractWsClient } from 'transport/wsClients';

import { useAuthStore } from './authStore';

/**
 * Per-market position entry tracked by the store. A trimmed projection of
 * PositionDTO / PositionVo carrying the full position scalar set, so the
 * shape is stable across snapshot hydration and WebSocket deltas and can be
 * turned into a derived `PortfolioPosition` via `positionScalarsFromEntry`.
 */
export interface EventPositionEntry {
    marketId?: string;
    marketTicker: string;
    position: number;
    positionCost: number;
    totalCost: number;
    buyingCost: number;
    realizedPnl: number;
    feesPaid: number;
    positionFeeCost?: number;
    finalPositionFeeCost?: number;
    yesBuyingFeeCost?: number;
    noBuyingFeeCost?: number;
    volume: number;
    finalPosition?: number;
    finalPositionCost?: number;
    isSettled: boolean;
    isClosed?: boolean;
    lastPosition: number;
}

/** Event-level fields from the last REST snapshot (WS deltas don't carry them). */
export interface EventPositionMeta {
    lastUpdatedTs: string;
    collateralReturn: number;
}

/** Adapt a store entry to the shared position-derivation input. */
export function positionScalarsFromEntry(entry: EventPositionEntry): PositionScalars {
    return {
        position: entry.position,
        positionCost: entry.positionCost,
        totalCost: entry.totalCost,
        buyingCost: entry.buyingCost,
        realizedPnl: entry.realizedPnl,
        feesPaid: entry.feesPaid,
        volume: entry.volume,
        finalPosition: entry.finalPosition ?? 0,
        finalPositionCost: entry.finalPositionCost ?? 0,
        lastPosition: entry.lastPosition,
        yesBuyingFeeCost: entry.yesBuyingFeeCost ?? null,
        noBuyingFeeCost: entry.noBuyingFeeCost ?? null,
        positionFeeCost: entry.positionFeeCost ?? null,
        finalPositionFeeCost: entry.finalPositionFeeCost ?? null,
        isSettled: entry.isSettled,
        isClosed: entry.isClosed ?? false,
        lastUpdatedTs: '',
        marketId: entry.marketId ?? '',
        marketTicker: entry.marketTicker,
    };
}

export interface UserPositionsActions {
    /**
     * Merge a batch snapshot into the store (e.g. paginated REST loads in the
     * TraderDrawer). Per-event upsert — leaves markets already in the store
     * (added by WS) intact even if this REST batch didn't list them, so a
     * straggler page can't drop a market the user just opened via WS.
     *
     * Pass `knownWsVersion` (the value from `getUserPositionsWsVersion()`
     * captured before the REST request was fired) to also protect markets a WS
     * delta updated while the request was in flight — those live entries are
     * kept rather than reverted to the stale snapshot.
     */
    hydrate: (eventPositions: EventPositionVo[], options?: { knownWsVersion?: number }) => void;
    /** Reset all positions (e.g. on logout). */
    reset: () => void;
    /** Apply a single WS delta. Exported for tests. */
    applyWsUpdate: (payload: PositionDTO) => void;
}

export interface UserPositionsState {
    /** Map of eventTicker → marketTicker → EventPositionEntry. */
    positions: Record<string, Record<string, EventPositionEntry>>;
    /** Map of eventTicker → event-level snapshot meta (REST-sourced only). */
    eventMeta: Record<string, EventPositionMeta>;
    actions: UserPositionsActions;
}

type SetFn = (
    partial: Partial<UserPositionsState> | ((state: UserPositionsState) => Partial<UserPositionsState>),
) => void;
type GetFn = () => UserPositionsState;

let storeSet: SetFn;
let storeGet: GetFn;

// Monotonic counter bumped on every WS write, plus the counter value at each
// market's last WS write. A REST caller captures the current version before
// firing its request (`getUserPositionsWsVersion()`) and passes it back to
// `hydrate` as `knownWsVersion`; hydrate then refuses to overwrite any market
// whose last WS write is newer than that marker — i.e. a `user_position` delta
// that landed while the (possibly pre-fill) REST snapshot was in flight. The
// counter stays monotonic for the tab's lifetime; only the per-market map is
// cleared on logout so a fresh session lets REST repopulate every market.
let wsWriteVersion = 0;
const wsWriteVersionByMarket = new Map<string, number>();

function marketVersionKey(eventTicker: string, marketTicker: string): string {
    return `${eventTicker}\u0000${marketTicker}`;
}

function recordWsWrite(eventTicker: string, marketTicker: string): void {
    wsWriteVersion += 1;
    wsWriteVersionByMarket.set(marketVersionKey(eventTicker, marketTicker), wsWriteVersion);
}

/** Current WS-write version — capture before a REST fetch, pass to `hydrate`. */
export function getUserPositionsWsVersion(): number {
    return wsWriteVersion;
}

function optionalCentsFromDollarsString(dollars: string | undefined): number | undefined {
    return centsFromDollarsStringOrNull(dollars) ?? undefined;
}

export function entryFromPositionVo(vo: PositionVo): EventPositionEntry {
    return {
        marketId: vo.market_id,
        marketTicker: vo.market_ticker,
        position: numberFromFixedPointString(vo.position_fp),
        positionCost: centsFromDollarsStringOrZero(vo.position_cost_dollars),
        totalCost: centsFromDollarsStringOrZero(vo.total_cost_dollars),
        buyingCost: centsFromDollarsStringOrZero(vo.buying_cost_dollars),
        realizedPnl: centsFromDollarsStringOrZero(vo.realized_pnl_dollars),
        feesPaid: centsFromDollarsStringOrZero(vo.fees_paid_dollars),
        positionFeeCost: optionalCentsFromDollarsString(vo.position_fee_cost_dollars),
        finalPositionFeeCost: optionalCentsFromDollarsString(vo.final_position_fee_cost_dollars),
        yesBuyingFeeCost: optionalCentsFromDollarsString(vo.yes_buying_fee_cost_dollars),
        noBuyingFeeCost: optionalCentsFromDollarsString(vo.no_buying_fee_cost_dollars),
        volume: numberFromFixedPointString(vo.volume_fp),
        finalPosition: numberFromFixedPointString(vo.final_position_fp),
        finalPositionCost: centsFromDollarsStringOrZero(vo.final_position_cost_dollars),
        isSettled: vo.is_settled,
        isClosed: vo.is_closed,
        lastPosition: numberFromFixedPointString(vo.last_position_fp),
    };
}

function entryFromPositionDto(dto: PositionDTO): EventPositionEntry {
    return {
        marketId: dto.market_id,
        marketTicker: dto.market_ticker,
        position: numberFromFixedPointString(dto.position_fp),
        positionCost: centsFromDollarsStringOrZero(dto.position_cost_dollars),
        totalCost: centsFromDollarsStringOrZero(dto.total_cost_dollars),
        buyingCost: centsFromDollarsStringOrZero(dto.buying_cost_dollars),
        realizedPnl: centsFromDollarsStringOrZero(dto.realized_pnl_dollars),
        feesPaid: centsFromDollarsStringOrZero(dto.fees_paid_dollars),
        positionFeeCost: optionalCentsFromDollarsString(dto.position_fee_cost_dollars),
        finalPositionFeeCost: optionalCentsFromDollarsString(dto.final_position_fee_cost_dollars),
        yesBuyingFeeCost: optionalCentsFromDollarsString(dto.yes_buying_fee_cost_dollars),
        noBuyingFeeCost: optionalCentsFromDollarsString(dto.no_buying_fee_cost_dollars),
        volume: numberFromFixedPointString(dto.volume_fp),
        finalPosition: numberFromFixedPointString(dto.final_position_fp),
        finalPositionCost: centsFromDollarsStringOrZero(dto.final_position_cost_dollars),
        isSettled: dto.is_settled,
        // The DTO doesn't carry `is_closed`; settled is the closest stand-in.
        isClosed: dto.is_settled,
        lastPosition: numberFromFixedPointString(dto.last_position_fp),
    };
}

function entriesEqual(a: EventPositionEntry, b: EventPositionEntry): boolean {
    return (
        a.position === b.position &&
        a.positionCost === b.positionCost &&
        a.totalCost === b.totalCost &&
        a.buyingCost === b.buyingCost &&
        a.realizedPnl === b.realizedPnl &&
        a.feesPaid === b.feesPaid &&
        a.positionFeeCost === b.positionFeeCost &&
        a.finalPositionFeeCost === b.finalPositionFeeCost &&
        a.yesBuyingFeeCost === b.yesBuyingFeeCost &&
        a.noBuyingFeeCost === b.noBuyingFeeCost &&
        a.volume === b.volume &&
        a.finalPosition === b.finalPosition &&
        a.finalPositionCost === b.finalPositionCost &&
        a.isSettled === b.isSettled &&
        a.isClosed === b.isClosed &&
        a.lastPosition === b.lastPosition
    );
}

function removeMarketEntry(
    positions: Record<string, Record<string, EventPositionEntry>>,
    eventTicker: string,
    marketTicker: string,
): Record<string, Record<string, EventPositionEntry>> {
    const eventMap = positions[eventTicker];
    if (!eventMap?.[marketTicker]) return positions;
    const nextEventMap = { ...eventMap };
    delete nextEventMap[marketTicker];
    const nextPositions = { ...positions };
    if (Object.keys(nextEventMap).length === 0) {
        delete nextPositions[eventTicker];
    } else {
        nextPositions[eventTicker] = nextEventMap;
    }
    return nextPositions;
}

function applyWsUpdate(payload: PositionDTO): void {
    const { event_ticker: eventTicker, market_ticker: marketTicker } = payload;
    if (!eventTicker || !marketTicker) return;

    const current = storeGet().positions;
    const currentEvent = current[eventTicker];
    const existing = currentEvent?.[marketTicker];

    // Settled markets are dropped from the live map — the drawer only shows unsettled positions.
    if (payload.is_settled) {
        if (!existing) return;
        recordWsWrite(eventTicker, marketTicker);
        storeSet((state) => ({ positions: removeMarketEntry(state.positions, eventTicker, marketTicker) }));
        return;
    }

    const next = entryFromPositionDto(payload);
    if (existing && entriesEqual(existing, next)) return;

    recordWsWrite(eventTicker, marketTicker);
    storeSet((state) => ({
        positions: {
            ...state.positions,
            [eventTicker]: {
                ...(state.positions[eventTicker] ?? {}),
                [marketTicker]: next,
            },
        },
    }));
}

function handleUserPositionMessages(response: WsResponse): void {
    const messages: WsMessage[] = Array.isArray(response) ? response : [response];
    const activeSubaccount = getSubaccountNumber();
    for (const msg of messages) {
        if (msg.type !== 'user_position') continue;
        const payload = msg.msg as PositionDTO;
        // Discard position updates for a subaccount the user isn't currently
        // scoped to. Treat a missing field as primary (subaccount 0) for BE
        // rollout.
        if ((payload.subaccount ?? 0) !== activeSubaccount) continue;
        rawPositionListeners.forEach((cb) => cb(payload));
        applyWsUpdate(payload);
    }
}

const rawPositionListeners = new Set<(payload: PositionDTO) => void>();

/**
 * Subscribe to the raw `user_position` WS payloads (already filtered to the
 * active subaccount). This is the single browser-side position feed — the
 * portfolio events store's sync hook and the per-event query cache patcher
 * both hang off it instead of opening their own socket subscriptions.
 */
export function subscribeToRawPosition(cb: (payload: PositionDTO) => void): () => void {
    rawPositionListeners.add(cb);
    return () => rawPositionListeners.delete(cb);
}

let wsCleanup: (() => void) | null = null;
let authUnsubscribe: (() => void) | null = null;
let subaccountUnsubscribe: (() => void) | null = null;

function connectIfLoggedIn(userId: string | null): void {
    if (userId && !wsCleanup) {
        wsCleanup = eventContractWsClient.subscribe(
            { channels: ['cdc_qe_user_positions'] },
            handleUserPositionMessages,
        );
    } else if (!userId && wsCleanup) {
        wsCleanup();
        wsCleanup = null;
        wsWriteVersionByMarket.clear();
        storeSet({ positions: {}, eventMeta: {} });
    }
}

function ensureSubscription(): void {
    if (authUnsubscribe) return;

    connectIfLoggedIn(useAuthStore.getState().userId);
    authUnsubscribe = useAuthStore.subscribe((state, prev) => {
        if (state.userId !== prev.userId) {
            connectIfLoggedIn(state.userId);
        }
    });

    if (!subaccountUnsubscribe) {
        // Drop the previous subaccount's positions the moment the active
        // subaccount changes — they belong to the old subaccount, and a
        // mounted event page would otherwise render stale holdings (the WS
        // subscription stays open and its handler now admits only the new
        // subaccount's messages). `switchSubaccount` also resets, but this
        // covers the capability-loss `subaccountEnabledUpdated(false)` path
        // that bypasses it. Mirrors `userOrdersStore`.
        subaccountUnsubscribe = useSubAccountStore.subscribe((state, prev) => {
            if (state.subaccountNumber === prev.subaccountNumber) return;
            wsWriteVersionByMarket.clear();
            storeSet({ positions: {}, eventMeta: {} });
        });
    }
}

export const useUserPositionsStore = create<UserPositionsState>()((set, get) => {
    storeSet = set;
    storeGet = get;

    return {
        positions: {},
        eventMeta: {},
        actions: {
            /**
             * Merge a REST snapshot batch into the store. For each event in the
             * batch, upserts its markets into the store — leaves markets already
             * in the store (added by WS) intact even if this REST batch didn't
             * list them.
             *
             * Why merge, not replace: REST responses can race with WS. If the
             * user places a trade between the REST snapshot and its arrival,
             * WS will have already added the new market; we must not drop it
             * when the older REST response lands. WS remains the source of
             * truth for deletions via `is_settled` in `applyWsUpdate`.
             */
            hydrate: (eventPositions: EventPositionVo[], options?: { knownWsVersion?: number }) => {
                if (eventPositions.length === 0) return;
                const knownWsVersion = options?.knownWsVersion;
                set((state) => {
                    // Identity-preserve unchanged entries so a panel revisit
                    // (REST returning the same data WS already delivered)
                    // doesn't bump `positions` and re-render every subscriber.
                    const next = { ...state.positions };
                    const nextMeta = { ...state.eventMeta };
                    let changed = false;
                    let metaChanged = false;
                    for (const ep of eventPositions) {
                        const existingMeta = state.eventMeta[ep.event_ticker];
                        if (
                            !existingMeta ||
                            existingMeta.lastUpdatedTs !== ep.last_updated_ts ||
                            existingMeta.collateralReturn !== ep.collateral_returned_cents
                        ) {
                            nextMeta[ep.event_ticker] = {
                                lastUpdatedTs: ep.last_updated_ts,
                                collateralReturn: ep.collateral_returned_cents,
                            };
                            metaChanged = true;
                        }
                        const existingEventMap = state.positions[ep.event_ticker];
                        const marketMap: Record<string, EventPositionEntry> = {};
                        let eventChanged = false;
                        for (const mp of ep.market_positions) {
                            if (mp.is_settled) continue;
                            const existing = existingEventMap?.[mp.market_ticker];
                            // A WS delta newer than this REST snapshot already
                            // updated (or removed) this market while the request
                            // was in flight — keep the live entry rather than
                            // reverting it (e.g. a fill the order panel reads
                            // straight from this store). If WS removed it, skip
                            // re-adding it.
                            if (
                                knownWsVersion !== undefined &&
                                (wsWriteVersionByMarket.get(marketVersionKey(ep.event_ticker, mp.market_ticker)) ?? 0) >
                                    knownWsVersion
                            ) {
                                if (existing) marketMap[mp.market_ticker] = existing;
                                continue;
                            }
                            const nextEntry = entryFromPositionVo(mp);
                            if (existing && entriesEqual(existing, nextEntry)) {
                                marketMap[mp.market_ticker] = existing;
                            } else {
                                marketMap[mp.market_ticker] = nextEntry;
                                eventChanged = true;
                            }
                        }
                        if (Object.keys(marketMap).length === 0) continue;
                        if (!eventChanged && existingEventMap) {
                            // All snapshot entries match; preserve any
                            // WS-added markets already in the existing map.
                            continue;
                        }
                        next[ep.event_ticker] = { ...existingEventMap, ...marketMap };
                        changed = true;
                    }
                    if (!changed && !metaChanged) return state;
                    return {
                        ...(changed ? { positions: next } : {}),
                        ...(metaChanged ? { eventMeta: nextMeta } : {}),
                    };
                });
            },
            reset: () => {
                wsWriteVersionByMarket.clear();
                set({ positions: {}, eventMeta: {} });
            },
            applyWsUpdate,
        },
    };
});

/**
 * Imperative (non-hook) lookup of a market's live position entry by market **id**,
 * scanning the eventTicker → marketTicker map. Replaces the deleted
 * `traderStore.portfolio.getHoldingInMarket(marketId)` for callers that need a holding
 * inside an effect/callback (not a render subscription) — e.g. PrimaryContent's
 * spread/total auto-selection and held-position payout polling. Components that render
 * off a holding should subscribe via `useMarketPosition`/`useEventMarketPositions` instead.
 */
export function getPositionEntryByMarketId(marketId: string): EventPositionEntry | undefined {
    if (!marketId) return undefined;
    const positions = useUserPositionsStore.getState().positions;
    for (const eventMap of Object.values(positions)) {
        for (const entry of Object.values(eventMap)) {
            if (entry.marketId === marketId) return entry;
        }
    }
    return undefined;
}

if (typeof window !== 'undefined') {
    ensureSubscription();
}
